import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

export default function RefundPolicy() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen pt-20 lg:pt-24 bg-white">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate("/support")}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft size={20} weight="bold" />
          <span className="font-medium">Back to Support</span>
        </button>

        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-black text-[#0F172A] mb-4">Return, Refund & Cancellation Policy</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            How material refunds, cancellation fees, and logistics liability are handled across the Loado marketplace.
          </p>
        </div>

        <div className="space-y-8 text-slate-600">
          <section className="space-y-4">
            <h2 className="font-bold text-[#0F172A] text-xl">1. Core Policy Overview</h2>
            <p>This policy governs two distinct services on the <strong>Loado App</strong>:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Material Marketplace:</strong> For purchasing construction materials (Cement, Steel, Bricks, etc.).</li>
              <li><strong>Logistics & Fleet Service:</strong> For booking vehicles (Bikes to Heavy Trucks).</li>
            </ul>
            <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-400 text-sm text-slate-700">
              <strong>Important:</strong> "Return & Refund" is only applicable to <strong>Material Purchases</strong>. Logistics/Fleet services are categorized as "Executed Labor" and are non-refundable once the service has been initiated.
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="font-bold text-[#0F172A] text-xl">2. Logistics & Fleet Service (Payment Liability)</h2>
            <p>Loado operates on a <strong>"Pay After Delivery"</strong> model for logistics to ensure customer satisfaction.</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Customer Liability:</strong> By booking a vehicle, the customer enters a binding contract to pay the <strong>Fleet Logistics Charge</strong> upon arrival at the destination or completion of the task.</li>
              <li><strong>Non-Negotiable Payment:</strong> Even if a material order is cancelled <em>after</em> the vehicle is on the way, the customer is <strong>liable to pay the full logistics charge</strong> to the Delivery Partner for their time, fuel, and vehicle wear.</li>
              <li><strong>Refusal to Pay:</strong> Failure to pay the Delivery Partner will result in a permanent ban from the Loado platform and potential legal recovery of dues.</li>
            </ul>
          </section>

          <section className="space-y-4 overflow-x-auto">
            <h2 className="font-bold text-[#0F172A] text-xl">3. Cancellation Policy (Material Marketplace)</h2>
            <p>Cancellations are tiered based on the stage of the order:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border border-slate-200">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-2 border-b border-slate-200 font-semibold">Order Stage</th>
                    <th className="text-left p-2 border-b border-slate-200 font-semibold">Cancellation Fee</th>
                    <th className="text-left p-2 border-b border-slate-200 font-semibold">Fleet Charge Liability</th>
                    <th className="text-left p-2 border-b border-slate-200 font-semibold">Refund Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="text-slate-600 border-b border-slate-200">
                    <td className="p-2"><strong>Stage 1:</strong> Before Partner Allocation</td>
                    <td className="p-2">2% Platform Fee</td>
                    <td className="p-2">Zero</td>
                    <td className="p-2">98% of Material Cost</td>
                  </tr>
                  <tr className="text-slate-600 border-b border-slate-200">
                    <td className="p-2"><strong>Stage 2:</strong> After Partner Allocation</td>
                    <td className="p-2">2% Platform Fee</td>
                    <td className="p-2">Full Fleet Charge</td>
                    <td className="p-2">Material Cost minus 2% minus Full Fleet Charge</td>
                  </tr>
                  <tr className="text-slate-600 border-b border-slate-200">
                    <td className="p-2"><strong>Stage 3:</strong> Loading / In-Transit</td>
                    <td className="p-2">7% Platform Fee with Unloading Fee</td>
                    <td className="p-2">Full Fleet Charge include Return</td>
                    <td className="p-2">Material Cost minus 7% minus Double Fleet Charge</td>
                  </tr>
                  <tr className="text-slate-600">
                    <td className="p-2"><strong>Stage 4:</strong> Material Unloaded</td>
                    <td className="p-2">No Cancellation</td>
                    <td className="p-2">Full Fleet Charge</td>
                    <td className="p-2 text-red-600 font-semibold">Zero (No Refund)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="font-bold text-[#0F172A] text-xl">4. Material Quality Disputes & Returns</h2>
            <p>We ensure quality, but in the rare event of a dispute:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Inspection Period:</strong> The Buyer must inspect all materials <strong>BEFORE UNLOADING</strong>. Once the material touches the ground at the construction site, it is considered "Accepted" and cannot be returned.</li>
              <li><strong>Rejection Criteria:</strong> If the material is damaged, incorrect, or sub-standard, the buyer must:
                <ol className="list-decimal pl-5 mt-1 space-y-1">
                  <li>Cancel the order and take and upload material images. Any other dispute notify Loado Support at <strong>support@loado.in</strong>.</li>
                  <li>Provide <strong>clear, timestamped photos</strong> of the material while it is still on the delivery vehicle.</li>
                </ol>
              </li>
              <li><strong>Full Refund:</strong> If the quality issue is verified, the buyer receives a <strong>100% refund</strong> of the material cost.</li>
              <li><strong>Seller Penalty:</strong> In verified quality disputes, the <strong>Fleet Charges</strong> and the <strong>2% Cancellation Fee</strong> will be forcibly deducted from the <strong>Seller&apos;s payout</strong> to reimburse the delivery partner and the platform.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-bold text-[#0F172A] text-xl">5. Non-Returnable Items</h2>
            <p>Due to the nature of construction materials, the following are strictly <strong>Non-Returnable</strong> once unloaded:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Perishables:</strong> Cement and Wall Putty (due to moisture sensitivity).</li>
              <li><strong>Loose Materials:</strong> Sand, Blue Metal (Aggregates), and Bricks.</li>
              <li><strong>Custom Cuts:</strong> Steel rebars or timber cut to specific site measurements.</li>
            </ul>
          </section>

          <section className="space-y-4">
            <h2 className="font-bold text-[#0F172A] text-xl">6. Refund Timelines</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>All approved refunds for Material Purchases will be credited back to the original payment source within <strong>5 to 7 business days</strong>.</li>
              <li>Any logistics charges paid at the time of booking (if any) are non-refundable if the vehicle reached the pickup point.</li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
